import { Component, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TherapistAuthStateService } from '@therapist/shared/services/state/therapist-auth-state.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Router, RouterLink } from '@angular/router';
import { catchError, switchMap, tap, throwError } from 'rxjs';
import { TherapistApiService } from '@therapist/shared/services/api/therapist-api.service';
import { TherapistDto } from '@therapist/shared/models/therapist/therapist.dto';
import { TherapistStateService } from '@therapist/shared/services/state/therapist-state.service';

@Component({
  selector: 'sana-tp-login-page',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    RouterLink
  ],
  templateUrl: './therapist-login-page.component.html',
  styleUrl: './therapist-login-page.component.scss'
})
export class TherapistLoginPageComponent implements OnInit {

  readonly #fb = inject(FormBuilder);
  readonly #authStateService = inject(TherapistAuthStateService);
  readonly #therapistStateService = inject(TherapistStateService);
  readonly #therapistApiService = inject(TherapistApiService);
  readonly #router = inject(Router);

  readonly loginForm = this.#fb.group({
    email: new FormControl<string | null>(null, [
      Validators.required,
      Validators.email
    ]),
    password: new FormControl<string | null>(null, [Validators.required])
  });

  isSubmitting = signal(false);
  hasLoginErrorMessage = signal(false);
  loginErrorMessage = signal('');
  showPassword = signal(false);

  hasPasswordField = false;

  readonly currentYear = new Date().getFullYear();

  ngOnInit() {
    if (this.#authStateService.hasValidToken()) {
      this.#router.navigate(['/']);
    }
  }

  enablePasswordField() {
    const emailCtrl = this.loginForm.controls.email;
    emailCtrl.markAsTouched();

    if (emailCtrl.invalid) {
      return;
    }

    this.hasPasswordField = true;
  }

  disablePasswordField() {
    this.hasPasswordField = false;
    this.hasLoginErrorMessage.set(false);
  }

  togglePassword() {
    this.showPassword.update(value => !value);
  }

  canSubmit(): boolean {
    return this.loginForm.valid && !this.isSubmitting();
  }

  onSubmit() {
    this.loginForm.markAllAsTouched();
    this.hasLoginErrorMessage.set(false);

    if (!this.canSubmit()) {
      return;
    }

    this.isSubmitting.set(true);

    const email = this.loginForm.controls.email.value!;
    const password = this.loginForm.controls.password.value!;

    this.doLoginApiCall(email, password);
  }

  private doLoginApiCall(email: string, password: string) {
    this.#therapistApiService
      .submitLogin(email!, password!)
      .pipe(
        // 1) Nach Login zuerst Token speichern
        tap(login => this.#authStateService.setAccessToken(login.token)),

        // 2) Dann mit gültigem Token das Profil laden
        switchMap(login => this.#therapistApiService.getProfile()),

        // 3) Fehlerbehandlung
        catchError((err: HttpErrorResponse) => {
          this.handleLoginError(err);
          return throwError(() => err);
        })
      )
      .subscribe(therapist => this.handleLoginSuccess(therapist));
  }

  private handleLoginSuccess(therapist: TherapistDto) {
    this.#therapistStateService.setCurrentTherapist(therapist);
    this.#router.navigate(['/']);
    this.isSubmitting.set(false);
  }

  private handleLoginError(err: HttpErrorResponse) {
    this.loginErrorMessage.set('E-Mail oder Passwort falsch. Bitte versuchen Sie es erneut.');
    this.hasLoginErrorMessage.set(true);
    this.isSubmitting.set(false);
  }
}
